package one.healthium.uscreen.ui.blocks

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.LineHeightStyle
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import com.slapps.cupertino.ExperimentalCupertinoApi
import one.healthium.ui.compose.components.cupertino.CupertinoWheelPicker
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.components.cupertino.rememberCupertinoPickerState
import one.healthium.uscreen.domain.models.NumberPickerBlock
import one.healthium.uscreen.ui.util.uscreenPadding

private val PickerTextStyle = TextStyle(
    fontSize = 50.sp,
    fontWeight = FontWeight.Bold,
    letterSpacing = (-1).sp,
    lineHeight = 1.5.em,
    lineHeightStyle = LineHeightStyle(
        alignment = LineHeightStyle.Alignment.Center,
        trim = LineHeightStyle.Trim.None,
    ),
)

private val LabelTextStyle = TextStyle(
    fontSize = 17.sp,
    fontWeight = FontWeight.Normal,
    letterSpacing = (-0.34).sp,
)

@OptIn(ExperimentalCupertinoApi::class)
@Composable
fun UScreenNumberPickerBlock(
    modifier: Modifier = Modifier,
    block: NumberPickerBlock,
    onNumberChanged: (key: String, value: Float) -> Unit,
) {
    val min = block.min
    val max = block.max
    val defaultValue = block.defaultValue
    val items = remember(min, max) { (min..max).toList() }
    val initialIndex = remember(min, max, defaultValue) {
        (defaultValue - min).coerceIn(0, max - min)
    }

    val pickerState = rememberCupertinoPickerState(
        infinite = false,
        initiallySelectedItemIndex = initialIndex,
    )

    LaunchedEffect(pickerState.selectedItemIndex, pickerState.isScrollInProgress) {
        if (pickerState.isScrollInProgress) return@LaunchedEffect
        val selectedIndex = pickerState.selectedItemIndex
        if (selectedIndex in items.indices) {
            onNumberChanged(block.key, items[selectedIndex].toFloat())
        }
    }

    Box(
        modifier = modifier
            .fillMaxWidth()
            .uscreenPadding(block.padding)
            .testTag("uscreen_number_picker"),
    ) {
        CupertinoWheelPicker(
            modifier = Modifier.fillMaxWidth(),
            state = pickerState,
            items = items,
            height = 375.dp,
            indicator = {},
            containerColor = AppTheme.uscreenStyle.pickerContainerColor,
            textStyle = PickerTextStyle,
        ) { value ->
            Text(
                text = value.toString(),
                style = PickerTextStyle,
                color = AppTheme.uscreenStyle.pickerTextColor,
            )
        }

        val label = block.label
        if (label != null) {
            // CupertinoWheelPicker renders text with fontScale=1, match that for measurement
            val density = LocalDensity.current
            val pickerDensity = remember(density) { Density(density.density, 1f) }
            val textMeasurer = rememberTextMeasurer()
            val labelOffset = remember(max, pickerDensity) {
                val maxValueText = max.toString()
                with(pickerDensity) {
                    textMeasurer.measure(maxValueText, PickerTextStyle).size.width.toDp() / 2 + 16.dp
                }
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth(0.5f)
                    .align(Alignment.CenterEnd),
                contentAlignment = Alignment.CenterStart,
            ) {
                Text(
                    text = label,
                    style = LabelTextStyle,
                    color = AppTheme.uscreenStyle.pickerTextColor,
                    modifier = Modifier.padding(start = labelOffset),
                )
            }
        }
    }
}
